/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  identity,
  optimisticLockingOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';

import { omit } from 'lodash';
import { Observable } from 'rxjs';

import {
  ComponentPropagateFormulaMapper,
  HpeCcOrderDetailsMapper,
  HpeDetailsInitMapper,
  HpeDetailsMapper,
  HpeDetailsSaveMapper,
  HpeGenerateItemIdsResponseMapper,
  HpeGetComponentsResponseMapper,
  HpeGetCostCenterRequestMapper,
  HpeGetCostCenterResponseMapper,
} from './mappers/hpe-details.mapper';
import {
  ComponentPropagateFormulaRequestCustomDto,
  HpeCcOrderDetailsDto,
  HpeDetailsDto,
  HpeDetailsInitDto,
  HpeDetailsSaveRequestDto,
  HpeGenerateItemIdsResponseDto,
  HpeGetComponentsResponseDto,
  HpeGetCostCenterRequestDto,
  HpeGetCostCenterResponseDto,
} from './models/hpe-details.dto';
import { ComponentRequest } from 'builder-shared-data/components';

@Injectable()
export class HousePlanEstimateRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  init(init: HpeDetailsInitDto): Observable<HpeDetailsDto> {
    return this.httpGet(
      `${this.bp}/house-plans/${init.housePlanId}/estimate`,
      omit(init, 'housePlanId'),
      HpeDetailsInitMapper,
      optimisticLockingOf(HpeDetailsMapper));
  }

  save(dto: HpeDetailsSaveRequestDto): Observable<HpeDetailsDto> {
    return this.httpPost(
      `${this.bp}/house-plans/${dto.housePlanId}/calculate`,
      omit(dto, 'housePlanId'),
      HpeDetailsSaveMapper,
      optimisticLockingOf(HpeDetailsMapper));
  }

  getOrderDetails(housePlanId: ID,
                  costCenterId: ID,
                  request: HpeGetCostCenterRequestDto): Observable<HpeCcOrderDetailsDto> {
    return this.httpPost(
      `${this.bp}/house-plans/${housePlanId}/estimate/cost-centers/${costCenterId}/order-details`,
      request,
      HpeGetCostCenterRequestMapper,
      HpeCcOrderDetailsMapper);
  }

  getCostCenter(housePlanId: ID,
                costCenterId: ID,
                request: HpeGetCostCenterRequestDto): Observable<HpeGetCostCenterResponseDto> {
    return this.httpPost(
      `${this.bp}/house-plans/${housePlanId}/estimate/cost-centers/${costCenterId}`,
      request,
      HpeGetCostCenterRequestMapper,
      HpeGetCostCenterResponseMapper);
  }

  getComponents(housePlanId: ID, components: ComponentRequest[]): Observable<HpeGetComponentsResponseDto> {
    return this.httpPost(
      `${this.bp}/house-plans/${housePlanId}/estimate/components`,
      { components },
      identity(),
      HpeGetComponentsResponseMapper);
  }

  generateItemIds(housePlanId: ID, count: number): Observable<HpeGenerateItemIdsResponseDto> {
    return this.httpPost(
      `${this.bp}/house-plans/${housePlanId}/estimate/generate-item-ids`,
      <any>{ count },
      identity(),
      HpeGenerateItemIdsResponseMapper);
  }

  findAllAsDataSource(filter: ComponentFormulaPropagateFilter):
    SlicedDataSource<ComponentPropagateFormulaRequestCustomDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/house-plans`,
      filter,
      ComponentPropagateFormulaMapper,
      arrayOf(ComponentPropagateFormulaMapper),
    );
  }

}

export class ComponentFormulaPropagateFilter {
  constructor(
    public showDeleted: boolean) {
  }
}
